import { cn } from "@/lib/utils";
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  FileText,
  Home,
  LogOut,
  Menu,
  MessageSquare,
  Mic,
  Moon,
  PiggyBank,
  Plus,
  Settings,
  Sun,
  Target,
  Wallet,
  SlidersHorizontal,
  CalendarClock,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuthContext } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useFirstTransaction } from "@/hooks/useFirstTransaction";
import { BillingReminder } from "./BillingReminder";
import type { User } from "../../../drizzle/schema";

function useNavItems() {
  const { t } = useI18n();
  return [
    { href: "/dashboard", icon: Home, label: t.nav.home },
    { href: "/transactions", icon: Wallet, label: t.nav.transactions },
    { href: "/charts", icon: BarChart3, label: t.nav.charts },
    { href: "/goals", icon: Target, label: t.nav.goals },
    { href: "/budgets", icon: PiggyBank, label: t.nav.budgets },
    { href: "/reports", icon: FileText, label: t.nav.reports },
    { href: "/bills", icon: CalendarClock, label: t.nav.bills },
  ];
}

function useBottomNavItems() {
  const { t } = useI18n();
  return [
    { href: "/dashboard", icon: Home, label: t.nav.home },
    { href: "/transactions", icon: Wallet, label: t.nav.transactions },
    { href: "/charts", icon: BarChart3, label: t.nav.charts },
    { href: "/goals", icon: Target, label: t.nav.goals },
    { href: "/reports", icon: FileText, label: t.nav.reports },
  ];
}

interface BolsoLayoutProps {
  children: React.ReactNode;
  onNewTransaction?: () => void;
  onVoiceEntry?: () => void;
}

interface SidebarContentProps {
  mobile?: boolean;
  collapsed: boolean;
  location: string;
  user: User | null;
  initials: string;
  theme: string;
  toggleTheme: (() => void) | undefined;
  logout: () => void;
  navigate: (path: string) => void;
  setMobileOpen: (open: boolean) => void;
  hasFirstTransaction: boolean;
}

function SidebarContent({
  mobile = false,
  collapsed,
  location,
  user,
  initials,
  theme,
  toggleTheme,
  logout,
  navigate,
  setMobileOpen,
  hasFirstTransaction,
}: SidebarContentProps) {
  const { t } = useI18n();
  const navItems = useNavItems();
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={cn("flex items-center gap-3 px-4 py-5", collapsed && !mobile && "justify-center px-2")}>
        <div className="w-9 h-9 rounded-xl bg-sidebar-primary flex items-center justify-center flex-shrink-0">
          <Wallet className="w-5 h-5 text-sidebar-primary-foreground" />
        </div>
        {(!collapsed || mobile) && (
          <span className="font-serif text-xl font-semibold text-sidebar-foreground">Bolso</span>
        )}
      </div>

      <Separator className="bg-sidebar-border mb-3" />

      {/* Nav Items */}
      <nav className="flex-1 px-2 space-y-1">
        {navItems.map(({ href, icon: Icon, label }) => {
          const active = location === href || (href !== "/dashboard" && location.startsWith(href));
          return (
            <Link
              key={href}
              to={href}
              onClick={() => mobile && setMobileOpen(false)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150",
                "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                active
                  ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
                  : "text-sidebar-foreground/80",
                collapsed && !mobile && "justify-center px-2"
              )}
            >
              <Icon className="w-4.5 h-4.5 flex-shrink-0" />
              {(!collapsed || mobile) && <span>{label}</span>}
            </Link>
          );
        })}
      </nav>

      {user?.role === "admin" && (
        <Link
          to="/admin"
          onClick={() => mobile && setMobileOpen(false)}
          className={cn(
            "mx-2 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
            "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            location === "/admin" && "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm",
            collapsed && !mobile && "justify-center px-2"
          )}
        >
          <ShieldCheck className="w-4.5 h-4.5 flex-shrink-0" />
          {(!collapsed || mobile) && <span>{t.nav.adminPanel}</span>}
        </Link>
      )}

      <Separator className="bg-sidebar-border mt-3 mb-3" />

      {/* Bottom actions */}
      <div className={cn("px-2 pb-4 space-y-1")}>
        <button
          onClick={toggleTheme}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
            "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            collapsed && !mobile && "justify-center px-2"
          )}
        >
          {theme === "dark" ? <Sun className="w-4.5 h-4.5 flex-shrink-0" /> : <Moon className="w-4.5 h-4.5 flex-shrink-0" />}
          {(!collapsed || mobile) && <span>{theme === "dark" ? t.nav.lightMode : t.nav.darkMode}</span>}
        </button>

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2 mt-2 rounded-lg hover:bg-sidebar-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                collapsed && !mobile && "justify-center px-2"
              )}
            >
              <Avatar className="w-8 h-8 flex-shrink-0">
                <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              {(!collapsed || mobile) && (
                <div className="min-w-0 text-left">
                  <p className="text-xs font-medium text-sidebar-foreground truncate">{user?.name ?? "Usuário"}</p>
                  <p className="text-xs text-sidebar-foreground/50 truncate">{user?.email ?? ""}</p>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-52 mb-1">
            <DropdownMenuItem
              onClick={() => navigate("/settings/preferences")}
              className="cursor-pointer gap-2"
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span>{t.settings.title}</span>
            </DropdownMenuItem>
            {hasFirstTransaction && (
              <DropdownMenuItem
                onClick={() => {
                  navigate("/settings/whatsapp");
                  if (mobile) setMobileOpen(false);
                }}
                className="cursor-pointer"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                <span>{t.nav.whatsapp}</span>
              </DropdownMenuItem>
            )}
            {user?.role === "admin" && (
              <DropdownMenuItem
                onClick={() => {
                  navigate("/admin");
                  if (mobile) setMobileOpen(false);
                }}
                className="cursor-pointer"
              >
                <Settings className="mr-2 h-4 w-4" />
                <span>{t.nav.adminPanel}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => logout?.()}
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>{t.nav.logout}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export function BolsoLayout({ children, onNewTransaction, onVoiceEntry }: BolsoLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname: location } = useLocation();
  const { user, logout } = useAuthContext();
  const { theme, toggleTheme } = useTheme();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { hasFirstTransaction } = useFirstTransaction();
  const bottomNavItems = useBottomNavItems();

  // Default voice handler: navigate to /voice if no custom handler provided
  const handleVoiceEntry = onVoiceEntry ?? (() => navigate("/voice"));

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  const sidebarProps: SidebarContentProps = {
    collapsed,
    location,
    user,
    initials,
    theme,
    toggleTheme,
    logout,
    navigate,
    setMobileOpen,
    hasFirstTransaction,
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden lg:flex flex-col bg-sidebar border-r border-sidebar-border transition-all duration-300 flex-shrink-0",
          collapsed ? "w-16" : "w-60"
        )}
      >
        <SidebarContent {...sidebarProps} />
        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute top-1/2 -translate-y-1/2 -right-3 w-6 h-6 rounded-full bg-sidebar-border border border-sidebar-border flex items-center justify-center hover:bg-sidebar-accent transition-colors z-10"
          style={{ position: "relative", alignSelf: "flex-end", margin: "-12px 0 0 auto", transform: "none" }}
        >
          {collapsed ? (
            <ChevronRight className="w-3 h-3 text-sidebar-foreground" />
          ) : (
            <ChevronLeft className="w-3 h-3 text-sidebar-foreground" />
          )}
        </button>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-sidebar border-b border-sidebar-border">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-sidebar-primary flex items-center justify-center">
              <Wallet className="w-4 h-4 text-sidebar-primary-foreground" />
            </div>
            <span className="font-serif text-lg font-semibold text-sidebar-foreground">Bolso</span>
          </div>
          <div className="flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-sidebar-foreground hover:bg-sidebar-accent">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 bg-sidebar border-sidebar-border">
                <SidebarContent {...sidebarProps} mobile />
              </SheetContent>
            </Sheet>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto pb-20 lg:pb-0">
          <BillingReminder />
          {children}
        </main>

        {/* Mobile Bottom Tab Bar */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border flex items-center justify-around px-2 py-2 z-50 safe-area-bottom">
          {bottomNavItems.map(({ href, icon: Icon, label }) => {
            const active = location === href || (href !== "/dashboard" && location.startsWith(href));
            return (
              <Link
                key={href}
                to={href}
                className={cn(
                  "flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all duration-150 min-w-0",
                  active ? "text-primary" : "text-muted-foreground"
                )}
              >
                <Icon className={cn("w-5 h-5 transition-transform", active && "scale-110")} />
                <span className="text-[10px] font-medium leading-none">{label}</span>
              </Link>
            );
          })}

          {/* FAB Voz */}
          <button
            onClick={handleVoiceEntry}
            className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all duration-150 text-muted-foreground"
          >
            <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center shadow-lg -mt-5 mb-0.5 transition-transform active:scale-95">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <span className="text-[10px] font-medium leading-none">{t.voice.title.split(" ")[0]}</span>
          </button>
          {/* FAB Novo */}
          <button
            onClick={onNewTransaction}
            className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all duration-150 text-muted-foreground"
          >
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-lg -mt-5 mb-0.5 transition-transform active:scale-95">
              <Plus className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-[10px] font-medium leading-none">{t.transactions.new.split(" ")[0]}</span>
          </button>
        </nav>
      </div>

      {/* Desktop FABs */}
      {/* Mantém os atalhos afastados do selo fixo "Made with Manus". */}
      <div className="hidden lg:flex fixed bottom-8 right-24 flex-col gap-3 z-50">
        <button
          onClick={handleVoiceEntry}
          title="Entrada por voz"
          aria-label="Abrir entrada por voz"
          className="w-12 h-12 rounded-full bg-emerald-600 text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform active:scale-95"
        >
          <Mic className="w-5 h-5" />
        </button>
        <button
          onClick={onNewTransaction}
          title="Novo lançamento"
          aria-label="Criar novo lançamento"
          className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-xl flex items-center justify-center hover:scale-105 transition-transform active:scale-95"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
}
